import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LOG_TYPES } from '../../../constants/logTypeRegistry';
import { getMoodOption } from '../../../constants/moodOptions';
import { buildMoodDocId, logMood } from '../../../services/moodService';
import {
  buildOptimisticMoodEntry,
  getCurrentUserDisplayLabel,
  getMoodSnapshot,
} from './mobileCaptureDashboardUtils';

const dispatchTimelineRefresh = (childId) => {
  window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
    detail: { childId },
  }));
};

const dispatchTimelineEntryCreated = (detail) => {
  window.dispatchEvent(new CustomEvent('captureez:timeline-entry-created', { detail }));
};

const buildClearedMoodEntry = ({ childId, moodDocId, moodOption, now }) => {
  const userLabel = getCurrentUserDisplayLabel();

  return {
    id: moodDocId,
    childId,
    collection: 'dailyCare',
    actionType: 'mood',
    type: 'mood',
    timelineType: 'mood',
    category: 'mood',
    logCategory: 'mood',
    timestamp: now,
    createdAt: now,
    updatedAt: now,
    value: null,
    moodValue: null,
    mood: null,
    title: '',
    titlePrefix: '',
    content: '',
    text: '',
    label: '',
    data: {
      level: null,
      value: null,
      source: 'mobile-dashboard',
      status: 'deleted',
    },
    status: 'deleted',
    icon: null,
    iconComponent: moodOption.iconComponent || null,
    color: LOG_TYPES.mood.palette.dot,
    categoryIcon: null,
    categoryIconComponent: moodOption.iconComponent || null,
    categoryColor: LOG_TYPES.mood.palette.dot,
    incidentCategoryId: 'mood',
    incidentCategoryLabel: 'Mood',
    incidentCategoryColor: LOG_TYPES.mood.palette.dot,
    incidentCategoryIcon: null,
    incidentCategoryIconComponent: moodOption.iconComponent || null,
    authorName: userLabel,
    loggedByUser: userLabel,
  };
};

const useMobileMoodFeedback = ({ activeChild, activeChildEntries }) => {
  const [moodToastOpen, setMoodToastOpen] = useState(false);
  const [moodToastMessage, setMoodToastMessage] = useState('');
  const [moodModalOpen, setMoodModalOpen] = useState(false);
  const moodUndoRef = useRef(null);

  const moodSnapshot = useMemo(() => getMoodSnapshot(activeChildEntries), [activeChildEntries]);
  const snapshotMood = useMemo(() => getMoodOption(moodSnapshot.raw), [moodSnapshot.raw]);
  const currentMoodLabel = moodSnapshot.hasMood ? snapshotMood?.label || null : null;

  const resetMoodFeedback = useCallback(() => {
    setMoodModalOpen(false);
    moodUndoRef.current = null;
  }, []);

  useEffect(() => () => {
    moodUndoRef.current = null;
  }, []);

  const handleMoodSave = useCallback(async (selectedMood) => {
    if (!activeChild?.id || !selectedMood) {
      return;
    }

    const previousMood = moodSnapshot.raw || 'Calm';
    try {
      await logMood(activeChild.id, selectedMood);

      const optimisticMoodEntry = buildOptimisticMoodEntry({
        childId: activeChild.id,
        moodValue: selectedMood,
        moodOption: getMoodOption(selectedMood),
      });

      dispatchTimelineEntryCreated(optimisticMoodEntry);

      moodUndoRef.current = {
        previousMood,
        currentMood: selectedMood,
      };
      setMoodToastMessage('Mood logged ✓');
      setMoodToastOpen(true);
    } catch (error) {
      console.error('Error logging mood from dashboard:', error);
      dispatchTimelineRefresh(activeChild.id);
    }
  }, [activeChild?.id, moodSnapshot.raw]);

  const handleMoodClear = useCallback(async () => {
    if (!activeChild?.id) {
      return;
    }

    const previousMood = moodSnapshot.raw || 'Calm';
    const now = new Date();
    const moodDocId = buildMoodDocId(activeChild.id, now);
    const moodOption = getMoodOption(previousMood);

    try {
      await logMood(activeChild.id, null);

      dispatchTimelineEntryCreated(buildClearedMoodEntry({
        childId: activeChild.id,
        moodDocId,
        moodOption,
        now,
      }));

      dispatchTimelineRefresh(activeChild.id);

      moodUndoRef.current = {
        previousMood,
        currentMood: null,
      };
      setMoodToastMessage('Mood cleared');
      setMoodToastOpen(true);
    } catch (error) {
      console.error('Error clearing mood from dashboard:', error);
      dispatchTimelineRefresh(activeChild.id);
    }
  }, [activeChild?.id, moodSnapshot.raw]);

  const handleUndoMood = useCallback(async () => {
    const undoState = moodUndoRef.current;
    if (!undoState?.previousMood || !activeChild?.id) {
      setMoodToastOpen(false);
      return;
    }

    try {
      await logMood(activeChild.id, undoState.previousMood);

      const optimisticMoodEntry = buildOptimisticMoodEntry({
        childId: activeChild.id,
        moodValue: undoState.previousMood,
        moodOption: getMoodOption(undoState.previousMood),
      });

      dispatchTimelineEntryCreated(optimisticMoodEntry);

      setMoodToastMessage('Mood restored');
      moodUndoRef.current = null;
    } catch (error) {
      console.error('Error undoing mood from dashboard:', error);
      dispatchTimelineRefresh(activeChild.id);
    } finally {
      setMoodToastOpen(true);
    }
  }, [activeChild?.id]);

  return {
    canUndoMood: Boolean(moodUndoRef.current?.previousMood),
    closeMoodModal: () => setMoodModalOpen(false),
    closeMoodToast: () => setMoodToastOpen(false),
    currentMoodLabel,
    handleMoodClear,
    handleMoodSave,
    handleUndoMood,
    moodModalOpen,
    moodToastMessage,
    moodToastOpen,
    openMoodModal: () => setMoodModalOpen(true),
    resetMoodFeedback,
  };
};

export default useMobileMoodFeedback;
